import {
  addPurchaseOrderFiles,
  addPurchaseOrderNote,
  addPurchaseOrderWatchlist,
  createMultiplePurchaseOrders,
  doVerifyPO,
  editMultiPurchaseOrderLines,
  getDownloadedPurchaseOrderLines,
  getDownloadedPurchaseOrders,
  getPOWatchlistLate,
  getPurchaseOrder,
  getPurchaseOrderFields,
  getPurchaseOrderLineFields,
  getPurchaseOrderLineStatus,
  getPurchaseOrderPOLines,
  getPurchaseOrderPriority,
  getPurchaseOrders,
  getPurchaseOrdersSelection,
  getPurchaseOrderStatus,
  getPurchaseOrderStatusV2,
  getPurchaseOrdersWatchlists,
  getPurchaseOrdersWatchlistsDetail,
  poProcess,
  processETAApprovalRequest,
  setCancelPurchaseOrders,
  setPOLineETA,
  setPOLines,
  setPurchaseOrderBills,
  setPurchaseOrderFiles,
  setPurchaseOrderNotes,
  updateMultiplePurchaseOrders,
  updatePurchaseOrderWatchlist,
} from "../slice/purchaseOrderSlice";
import { apiSlice } from "./apiSlice";
import { purchaseAllocationInvalidationTags } from "./purchaseAllocationCacheInvalidation";
import { purchaseOrderFacilityCostingInvalidationTags } from "./purchaseOrderFacilityCostingInvalidation";
import { purchaseOrderDocumentInvalidationTags } from "./purchaseOrderWriteInvalidation";
import { fileResponseHandler } from "./utils";

/**
 * Show a status change on the board before the server confirms it.
 * @param {Function} dispatch
 * @param {Function} getState
 * @param {Array<number|string>} ids
 * @param {string} status
 * @return {Array<{undo: function(): void}>}
 */
function patchPurchaseOrderStatusCaches(dispatch, getState, ids, status) {
  const idSet = new Set((ids || []).map(id => Number(id)));
  const queries = getState()[apiSlice.reducerPath]?.queries || {};
  const fromById = new Map();
  Object.values(queries).forEach(entry => {
    if (entry?.endpointName !== "getPurchaseOrders") return;
    (entry.data?.results || []).forEach(order => {
      const id = Number(order.id);
      if (idSet.has(id) && !fromById.has(id)) {
        fromById.set(id, order.status);
      }
    });
  });

  const patches = [];
  Object.values(queries).forEach(entry => {
    if (!entry?.data) return;
    if (entry.endpointName === "getPurchaseOrders") {
      patches.push(
        dispatch(
          purchaseOrderApiSlice.util.updateQueryData(
            "getPurchaseOrders",
            entry.originalArgs,
            draft => {
              (draft.results || []).forEach(order => {
                if (idSet.has(Number(order.id))) {
                  order.status = status;
                }
              });
            },
          ),
        ),
      );
    }
    if (entry.endpointName === "getPurchaseOrderBoardCounts" && entry.data.counts) {
      patches.push(
        dispatch(
          purchaseOrderApiSlice.util.updateQueryData(
            "getPurchaseOrderBoardCounts",
            entry.originalArgs,
            draft => {
              if (!draft.counts) return;
              fromById.forEach(fromStatus => {
                if (!fromStatus || fromStatus === status) return;
                if (draft.counts[fromStatus] != null) {
                  draft.counts[fromStatus] = Math.max(0, Number(draft.counts[fromStatus]) - 1);
                }
                draft.counts[status] = Number(draft.counts[status] || 0) + 1;
              });
            },
          ),
        ),
      );
    }
  });
  return patches;
}

export const purchaseOrderApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    cancelPurchaseOrders: builder.mutation({
      query: data => ({ url: "/api_po/purchase_order/bulk_cancel/", method: "PATCH", body: data }),
      invalidatesTags: ["purchaseOrderList"],
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => dispatch(setCancelPurchaseOrders(data))).catch(error => {});
      },
    }),
    bulkUpdatePurchaseOrderStatus: builder.mutation({
      query: data => ({
        url: "/api_po/purchase_order/bulk_update_status/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["purchaseOrderList"],
      async onQueryStarted({ ids, status }, { dispatch, getState, queryFulfilled }) {
        const patches = patchPurchaseOrderStatusCaches(dispatch, getState, ids, status);
        try {
          await queryFulfilled;
        } catch (_) {
          patches.forEach(patch => patch.undo());
        }
      },
    }),
    confirmDraftPurchaseOrders: builder.mutation({
      query: data => ({
        url: "/api_po/purchase_order/confirm_draft/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["purchaseOrderList"],
    }),
    closePurchaseOrderPreview: builder.mutation({
      query: data => ({
        url: "/api_po/purchase_order/close_preview/",
        method: "POST",
        body: data,
      }),
    }),
    closePurchaseOrders: builder.mutation({
      query: data => ({
        url: "/api_po/purchase_order/close/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["purchaseOrderList", "purchaseOrderDetail"],
    }),
    reopenPurchaseOrder: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api_po/purchase_order/${id}/reopen/`,
        method: "PATCH",
        body: { reason },
      }),
      invalidatesTags: ["purchaseOrderList", "purchaseOrderDetail"],
    }),
    createConsolidatedPO: builder.mutation({
      query: payload => ({
        url: "/api_po/purchase_order/create_consolidated_po/",
        method: "post",
        body: payload,
      }),
    }),
    createMultiplePurchaseOrders: builder.mutation({
      query: data => ({
        url: "/api_po/purchase_order/create_multiple_purchase_orders/",
        method: "post",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(createMultiplePurchaseOrders(result.data.data)))
          .catch(error => {});
      },
    }),
    createPurchaseOrder: builder.mutation({
      query: data => ({ url: "/api_po/purchase_order/", method: "post", body: data }),
      invalidatesTags: (result, error, arg) => [
        ...purchaseOrderDocumentInvalidationTags(result, error, arg),
        ...purchaseOrderFacilityCostingInvalidationTags(result, error, arg),
      ],
    }),
    createDropshipPO: builder.mutation({
      query: data => ({
        url: "/api_po/purchase_order/create_dropship_po/",
        method: "post",
        body: data,
      }),
    }),
    createPurchaseOrderBill: builder.mutation({
      query: data => ({ url: `/api_invoice/bill/`, method: "POST", body: data }),
    }),
    createPurchaseOrderFiles: builder.mutation({
      query: data => ({
        url: `/api_docdigit/document/create_po_files/`,
        method: "POST",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(addPurchaseOrderFiles(result.data)))
          .catch(error => {});
      },
    }),
    createPurchaseOrderNote: builder.mutation({
      query: data => ({ url: "/api_chat/note/", method: "POST", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled, getState }) => {
        queryFulfilled
          .then(result => {
            if (getState().purchaseOrder.purchaseOrder?.notes?.length === 0) {
              dispatch(purchaseOrderApiSlice.util.invalidateTags(["purchaseOrderList"]));
            }
            dispatch(addPurchaseOrderNote(result.data));
          })
          .catch(error => {});
      },
    }),
    createPurchaseOrdersWatchlist: builder.mutation({
      query: data => ({ url: "/api_po/watchlist/", method: "post", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result =>
            dispatch(addPurchaseOrderWatchlist({ data: result.data.data, message: "create" })),
          )
          .catch(error => {});
      },
    }),
    downloadPurchaseOrderPdf: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_po/purchase_order/${id}/download_pdf/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    downloadPurchaseOrderExcel: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_po/purchase_order/${id}/download_excel/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    sharePurchaseOrder: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_po/purchase_order/${id}/share/`,
        method: "POST",
        body: data,
      }),
    }),
    getBillsForPurchaseOrder: builder.query({
      query: params => `/api_invoice/bill/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setPurchaseOrderBills(result.data)))
          .catch(error => {});
      },
    }),
    getDownloadedPurchaseOrderLines: builder.query({
      query: params =>
        params
          ? `/api_po/po_line/download_po_lines_records/?${params}`
          : "/api_po/po_line/download_po_lines_records/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getDownloadedPurchaseOrderLines(result.data.data)))
          .catch(error => {});
      },
    }),
    getDownloadedPurchaseOrders: builder.query({
      query: params =>
        params
          ? `/api_po/purchase_order/download_records/?${params}`
          : "/api_po/purchase_order/download_records/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getDownloadedPurchaseOrders(result.data.data)))
          .catch(error => {});
      },
    }),
    getFilesForPurchaseOrder: builder.query({
      query: params => `/api_docdigit/document/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setPurchaseOrderFiles(result.data)))
          .catch(error => {});
      },
    }),
    getPOLines: builder.query({
      query: (params = null) => (params ? `/api_po/po_line/?${params}` : "/api_po/po_line/"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setPOLines(result.data))).catch(error => {});
      },
    }),
    getPOWatchlistLate: builder.query({
      query: () => "/api_po/late_purchase_order/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getPOWatchlistLate(result.data))).catch(error => {});
      },
    }),
    getPurchaseOrderDetail: builder.query({
      query: (id, params = null) =>
        params ? `/api_po/purchase_order/${id}/?${params}` : `/api_po/purchase_order/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getPurchaseOrder(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [
        { type: "purchaseOrderDetail", id: String(id) },
        { type: "purchaseOrderDetail", id: Number(id) },
      ],
    }),
    getPurchaseOrderForPrepayment: builder.query({
      query: id => `/api_po/purchase_order/${id}/`,
    }),
    getPurchaseOrderFields: builder.query({
      query: () => "/api_po/purchase_order_fields/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderFields(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderLineFields: builder.query({
      query: () => "/api_po/purchase_order_line_fields/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderLineFields(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderLineStatus: builder.query({
      query: () => "/api_po/purchase_order_line_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderLineStatus(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderNotes: builder.query({
      query: params => (params ? `/api_chat/note/?${params}` : "/api_chat/note/"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setPurchaseOrderNotes(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderPOLines: builder.query({
      query: params => `/api_po/get_po_lines/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderPOLines(result.data.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderPriority: builder.query({
      query: () => "/api_po/purchase_order_priority/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderPriority(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrders: builder.query({
      query: (params = null) =>
        params ? `/api_po/purchase_order/?${params}` : "/api_po/purchase_order/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getPurchaseOrders(result.data))).catch(error => {});
      },
      providesTags: _ => ["purchaseOrderList"],
    }),
    getPurchaseOrderBoardCounts: builder.query({
      query: params => `/api_po/purchase_order/board_counts/?${params}`,
      providesTags: ["purchaseOrderList"],
    }),
    getPurchaseOrdersSelection: builder.query({
      providesTags: ["purchaseOrderList"],
      query: arg => {
        const raw =
          typeof arg === "string"
            ? arg
            : typeof arg?.params === "string"
              ? arg.params
              : arg?.params && typeof arg.params === "object"
                ? new URLSearchParams(
                    Object.entries(arg.params).reduce((acc, [key, value]) => {
                      if (value != null && value !== "") {
                        acc[key] = String(value);
                      }
                      return acc;
                    }, {}),
                  ).toString()
                : "";
        return raw
          ? `/api_po/purchase_order/?mode=selection&${raw}`
          : "/api_po/purchase_order/?mode=selection";
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrdersSelection(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderStatus: builder.query({
      query: (params = null) =>
        params ? `/api_po/purchase_order_status/?${params}` : "/api_po/purchase_order_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderStatus(result.data.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderStatusV2: builder.query({
      query: () => "/api_po/v2/purchase_order_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderStatusV2(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrderHistory: builder.query({
      query: ({ id, limit = 20, offset = 0 }) => ({
        url: `/api_po/purchase_order/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    getReceiptHistory: builder.query({
      query: ({ id, limit = 20, offset = 0 }) => ({
        url: `/api_po/receipt/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    getPurchaseOrdersWatchlist: builder.query({
      query: params => `/api_po/watchlist/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrdersWatchlists(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseOrdersWatchlistDetail: builder.query({
      query: params => `/api_po/watchlist/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrdersWatchlistsDetail(result.data.data)))
          .catch(error => {});
      },
    }),
    handleETAApprovalRequest: builder.mutation({
      query: ({ arId, data }) => ({
        url: `/api_affirm/approval_request/${arId}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(processETAApprovalRequest(result.data)))
          .catch(error => {});
      },
    }),
    poProcess: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_po/purchase_order/${id}/po_process/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(poProcess(result.data))).catch(error => {});
      },
    }),

    shareatchlistDirectMail: builder.mutation({
      query: data => ({ url: "api_po/watchlist_share_email/", method: "POST", body: data }),
    }),
    updateDeletePurchaseOrdersWatchlist: builder.mutation({
      query: data => ({ url: "/api_po/update_watchlist/", method: "post", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result =>
            dispatch(addPurchaseOrderWatchlist({ data: result.data.data, message: "update" })),
          )
          .catch(error => {});
      },
    }),
    updateMultiplePurchaseOrders: builder.mutation({
      query: data => ({
        url: "/api_po/purchase_order/update_downloaded_purchase_orders/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateMultiplePurchaseOrders(result.data.data)))
          .catch(error => {});
      },
    }),
    updateMultiPurchaseOrderLinesETA: builder.mutation({
      query: data => ({ url: "/api_po/po_line/bulk_update_eta/", method: "PATCH", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(editMultiPurchaseOrderLines(result.data)))
          .catch(error => {});
      },
    }),
    updatePOLineETA: builder.mutation({
      query: ({ poLineId, data }) => ({
        url: `/api_po/po_line_eta/${poLineId}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setPOLineETA(result.data))).catch(error => {});
      },
    }),
    updatePurchaseOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_po/purchase_order/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        ...purchaseOrderDocumentInvalidationTags(result, error, arg),
        ...purchaseAllocationInvalidationTags(result, error, arg),
        ...purchaseOrderFacilityCostingInvalidationTags(result, error, arg),
      ],
    }),
    updatePurchaseOrdersWatchlist: builder.mutation({
      query: data => ({ url: `/api_po/watchlist/${data.id}/`, method: "PATCH", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updatePurchaseOrderWatchlist(result.data.data)))
          .catch(error => {});
      },
    }),
    verifyPO: builder.mutation({
      query: data => ({
        url: `/api_po/purchase_order/dispatch_verification_notification/`,
        method: "POST",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(doVerifyPO(result.data))).catch(error => {});
      },
    }),
  }),
});

export const {
  useBulkUpdatePurchaseOrderStatusMutation,
  useConfirmDraftPurchaseOrdersMutation,
  useClosePurchaseOrderPreviewMutation,
  useClosePurchaseOrdersMutation,
  useReopenPurchaseOrderMutation,
  useCancelPurchaseOrdersMutation,
  useCreateConsolidatedPOMutation,
  useCreateMultiplePurchaseOrdersMutation,
  useCreateDropshipPOMutation,
  useCreatePurchaseOrderBillMutation,
  useCreatePurchaseOrderFilesMutation,
  useCreatePurchaseOrderMutation,
  useCreatePurchaseOrderNoteMutation,
  useCreatePurchaseOrdersWatchlistMutation,
  useGetBillsForPurchaseOrderQuery,
  useGetFilesForPurchaseOrderQuery,
  useGetPOLinesQuery,
  useGetPOWatchlistLateQuery,
  useGetPurchaseOrderDetailQuery,
  useGetPurchaseOrderFieldsQuery,
  useGetPurchaseOrderLineFieldsQuery,
  useGetPurchaseOrderLineStatusQuery,
  useGetPurchaseOrderNotesQuery,
  useGetPurchaseOrderPriorityQuery,
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderBoardCountsQuery,
  useGetPurchaseOrdersSelectionQuery,
  useGetPurchaseOrderStatusQuery,
  useGetPurchaseOrderStatusV2Query,
  useLazyGetPurchaseOrderStatusV2Query,
  useGetPurchaseOrdersWatchlistDetailQuery,
  useGetPurchaseOrdersWatchlistQuery,
  useGetPurchaseOrderHistoryQuery,
  useGetReceiptHistoryQuery,
  useHandleETAApprovalRequestMutation,
  useLazyDownloadPurchaseOrderPdfQuery,
  useLazyDownloadPurchaseOrderExcelQuery,
  useSharePurchaseOrderMutation,
  useLazyGetDownloadedPurchaseOrderLinesQuery,
  useLazyGetDownloadedPurchaseOrdersQuery,
  useLazyGetPOLinesQuery,
  useLazyGetPurchaseOrderDetailQuery,
  useLazyGetPurchaseOrderForPrepaymentQuery,
  useLazyGetPurchaseOrderPOLinesQuery,
  useLazyGetPurchaseOrdersQuery,
  useLazyGetPurchaseOrdersSelectionQuery,
  usePoProcessMutation,
  useShareatchlistDirectMailMutation,
  useUpdateDeletePurchaseOrdersWatchlistMutation,
  useUpdateMultiplePurchaseOrdersMutation,
  useUpdateMultiPurchaseOrderLinesETAMutation,
  useUpdatePOLineETAMutation,
  useUpdatePurchaseOrderMutation,
  useUpdatePurchaseOrdersWatchlistMutation,
  useVerifyPOMutation,
} = purchaseOrderApiSlice;
